import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { getServiceByKey, getOrderPath } from '@/lib/servicesCatalog';
import { cn } from '@/lib/utils';

/**
 * OrderServiceCTA - Reusable CTA component for service ordering
 * 
 * Props:
 * - serviceKey: The service key (photo_restoration, lora_training, photo_scanning)
 * - orderPath: Override the order path directly (optional, uses catalog if not provided)
 * - variant: "button" | "link" | "gold" | "outline" | "dark" (default: "button")
 * - label: Override the button label (optional)
 * - className: Additional CSS classes
 * - size: "sm" | "default" | "lg" (default: "default")
 * - showArrow: Show arrow icon (default: true)
 * - fullWidth: Make button full width (default: false)
 */
export function OrderServiceCTA({
  serviceKey,
  orderPath: orderPathOverride,
  variant = "button",
  label,
  className,
  size = "default",
  showArrow = true,
  fullWidth = false,
  ...props
}) {
  const navigate = useNavigate();
  
  // Get service info from catalog
  const service = serviceKey ? getServiceByKey(serviceKey) : null;
  const targetPath = orderPathOverride || (service ? service.orderPath : "/orders/new");
  const buttonLabel = label || (service ? service.ctaLabel : "Order Now");
  
  // Size classes
  const sizeClasses = {
    sm: "h-9 px-4 text-xs",
    default: "h-11 px-6 text-sm",
    lg: "h-13 px-10 text-sm",
  };
  
  // Variant classes
  const variantClasses = {
    button: "bg-[#050816] text-white hover:bg-[#050816]/90",
    gold: "bg-[#C9A962] text-[#0f1724] hover:bg-[#C9A962]/90 shadow-lg hover:shadow-xl",
    outline: "border-[#C9A962]/30 text-[#C9A962] hover:bg-[#C9A962]/10 hover:border-[#C9A962]/50 bg-transparent",
    dark: "bg-[#0f1724] text-white hover:bg-[#0f1724]/90 border border-white/10",
    link: "text-[#C9A962] hover:text-[#C9A962]/80 underline-offset-4 hover:underline p-0 h-auto",
  };
  
  const baseClasses = cn(
    "font-semibold uppercase tracking-[0.1em] transition-all duration-300 rounded-lg inline-flex items-center justify-center gap-2",
    sizeClasses[size],
    variantClasses[variant],
    fullWidth && "w-full",
    className
  );
  
  // Handle click
  const handleClick = (e) => {
    e.preventDefault();
    navigate(targetPath);
  };
  
  // For link variant, use a simpler approach
  if (variant === "link") {
    return (
      <Link 
        to={targetPath}
        className={cn(
          "font-medium text-[#C9A962] hover:text-[#C9A962]/80 transition-colors inline-flex items-center gap-1",
          className
        )}
        data-testid={`order-cta-${serviceKey || 'generic'}`}
        {...props}
      >
        {buttonLabel}
        {showArrow && <ArrowRight className="w-4 h-4" />}
      </Link>
    );
  }
  
  return (
    <Button
      onClick={handleClick}
      className={baseClasses}
      data-testid={`order-cta-${serviceKey || 'generic'}`}
      {...props}
    >
      {buttonLabel}
      {showArrow && <ChevronRight className="w-4 h-4 ml-1" />}
    </Button>
  );
}

/**
 * ServiceOrderCard - Card component for service selection
 * 
 * Props:
 * - service: Service object from catalog
 * - variant: "default" | "dark"
 * - draftInfo: { count: number, latest_order_id: string } - If user has draft orders
 * - isHighlighted: boolean - Apply visual highlight (recommended service)
 */
export function ServiceOrderCard({ 
  service, 
  variant = "default", 
  draftInfo = null,
  isHighlighted = false 
}) {
  const navigate = useNavigate();
  
  const hasDraft = draftInfo && draftInfo.count > 0;
  
  const cardClasses = cn(
    "group relative p-6 rounded-2xl border transition-all duration-300 cursor-pointer",
    variant === "dark" 
      ? "bg-[#141c2b] border-white/10 hover:border-[#C9A962]/30"
      : "bg-white border-gray-200 hover:border-[#C9A962]/50 hover:shadow-lg",
    // Apply highlight styling
    isHighlighted && "ring-2 ring-[#C9A962] border-[#C9A962]/50"
  );
  
  const titleClasses = variant === "dark" 
    ? "text-white" 
    : "text-gray-900";
    
  const descClasses = variant === "dark"
    ? "text-white/60"
    : "text-gray-600";
  
  // Navigate to existing draft or start new order
  const handleClick = () => {
    if (hasDraft) {
      // Navigate directly to the existing draft order
      navigate(`/orders/${draftInfo.latest_order_id}`);
    } else {
      navigate(service.orderPath);
    }
  };
  
  return (
    <div 
      className={cardClasses}
      onClick={handleClick}
      data-testid={`service-card-${service.key}`}
    >
      {/* Draft badge - top left */}
      {hasDraft && (
        <div className="absolute top-4 left-4">
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#C9A962] text-[#0f1724] animate-pulse">
            Continue Draft
          </span>
        </div>
      )}
      
      {/* Recommended badge - shows when highlighted */}
      {isHighlighted && !hasDraft && (
        <div className="absolute top-4 left-4">
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#C9A962]/20 text-[#C9A962] border border-[#C9A962]/30">
            Recommended
          </span>
        </div>
      )}
      
      {/* Price badge - top right */}
      <div className="absolute top-4 right-4">
        <span className={cn(
          "text-xs font-medium px-2 py-1 rounded-full",
          variant === "dark" 
            ? "bg-[#C9A962]/20 text-[#C9A962]"
            : "bg-[#C9A962]/10 text-[#C9A962]"
        )}>
          {service.priceRange}
        </span>
      </div>
      
      {/* Content - add top padding when badges are shown */}
      <div className={cn((hasDraft || isHighlighted) && "mt-6")}>
        <h3 className={cn("font-serif text-xl mb-2", titleClasses)}>
          {service.title}
        </h3>
        <p className={cn("text-sm mb-4 leading-relaxed", descClasses)}>
          {service.shortDescription}
        </p>
      </div>
      
      {/* Features */}
      <ul className="space-y-1.5 mb-6">
        {service.features.slice(0, 3).map((feature, idx) => (
          <li key={idx} className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-[#C9A962]"></span>
            <span className={cn("text-xs", descClasses)}>{feature}</span>
          </li>
        ))}
      </ul>
      
      {/* CTA - changes based on draft status */}
      <div className="flex items-center gap-2 text-[#C9A962] font-medium text-sm group-hover:gap-3 transition-all">
        <span>{hasDraft ? "Continue Order" : "Start Order"}</span>
        <ArrowRight className="w-4 h-4" />
      </div>
    </div>
  );
}

export default OrderServiceCTA;
